// myListApi.js
// Marc's List on the board. READS come straight from SharePoint (My_List via
// getItems — the app is a window, SharePoint is the truth). WRITES go through
// the Netlify relay to the App Service /my-list route, the same door chat
// uses (the my_list tool), so the card side happens with every write: a
// dated Notes line, a note source row, a synopsis re-read. Never PATCH
// My_List or Watch_Open from here.

import { getItems } from '../api/figApi';

const ENDPOINT = '/api/my-list';
export const DONE_KEEP_DAYS = 1;
export const CLOSE_RE = /\bclose\s+(the\s+)?(whole\s+)?(card|board)\b/i;

const dateOnly = (v) => (typeof v === 'string' ? v.slice(0, 10) : '');

/** My_List row → task. Same shape the server returns from `list`/`status`. */
export function toTask(r) {
  const wid = Number(r.WatchId);
  return {
    taskId: r.TaskId || null,
    id: r.id ?? null,
    task: r.Title || '',
    owner: r.Owner || 'Marc',
    status: ['Open', 'Done', 'Dropped'].includes(r.Status) ? r.Status : 'Open',
    note: r.Note || '',
    watchId: r.WatchId !== null && r.WatchId !== undefined && r.WatchId !== '' && Number.isFinite(wid) ? wid : null,
    projectId: r.ProjectId || null,
    doneAt: dateOnly(r.DoneAt) || null,
    sortOrder: r.SortOrder == null || r.SortOrder === '' ? null : Number(r.SortOrder),
    source: r.Source || '',
    created: typeof r.Created === 'string' ? r.Created : null,
  };
}

export function ymdOf(d) {
  // Chicago calendar day, matching the server's toCT.
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const g = (t) => p.find((x) => x.type === t).value;
  return `${g('year')}-${g('month')}-${g('day')}`;
}
export function addDaysYmd(ymd, n) {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Open tasks in order, then done ones from the last DONE_KEEP_DAYS, for one owner. */
export function visibleTasks(tasks, { owner = 'Marc', today, keepDays = DONE_KEEP_DAYS } = {}) {
  const cutoff = today ? addDaysYmd(today, -keepDays) : '0000-00-00';
  const byOrder = (a, b) => (a.sortOrder ?? 1e9) - (b.sortOrder ?? 1e9) || String(a.created || '').localeCompare(String(b.created || ''));
  const mine = (tasks || []).filter((t) => t.owner === owner);
  const open = mine.filter((t) => t.status === 'Open').sort(byOrder);
  const done = mine.filter((t) => t.status === 'Done' && t.doneAt && t.doneAt >= cutoff).sort((a, b) => String(b.doneAt).localeCompare(String(a.doneAt)) || byOrder(a, b));
  return [...open, ...done];
}

/** The set of WatchIds that open tasks point at — what ranks a card LISTED in NOW. */
export function listedWatchIds(tasks, owner = 'Marc') {
  return new Set((tasks || []).filter((t) => t.owner === owner && t.status === 'Open' && t.watchId).map((t) => t.watchId));
}

export async function loadMyList(token) {
  try {
    return { tasks: (await getItems(token, 'My_List')).map(toTask), error: null };
  } catch (e) {
    return { tasks: [], error: e?.message || String(e) };
  }
}

/**
 * One call, one action. Mirrors the my_list tool's input. The relay forces
 * dryRun:false and source:'app'. Returns the server's result as is.
 * opts.keepalive: true on a flush from beforeunload.
 */
export async function myListAction(getIdToken, payload, opts = {}) {
  const doFetch = async (forceRefresh) => fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getIdToken(forceRefresh)}` },
    body: JSON.stringify(payload),
    ...(opts.keepalive === true ? { keepalive: true } : {}),
  });
  let res = await doFetch(false);
  if (res.status === 401) res = await doFetch(true);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.upstreamStatus ? ` (upstream ${err.upstreamStatus})` : '';
    throw new Error((err.error || `my-list ${payload.action} failed (${res.status})`) + detail);
  }
  return res.json();
}
